import { Avatar, AvatarFallback, AvatarImage } from '@workspace/ui/components/avatar'
import { FormField } from '@workspace/ui/components/form-field'
import { TextInput } from '@workspace/ui/components/text-input'
import { getInitials } from '@workspace/ui/lib/utils'
import { Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useImageUpload } from '../../../hooks/use-image-upload'

interface MemberCardProps {
  member: {
    id: string
    name: string
    email: string
    position: string
    imageUrl: string | null
    linkedinUrl: string | null
    isLinked: boolean
  }
  onUpdate: (data: {
    name?: string
    email?: string
    position?: string
    imageUrl?: string | null
    linkedinUrl?: string | null
  }) => void
  onDelete: () => void
  isUpdating?: boolean
}

export function MemberCard({ member, onUpdate, onDelete, isUpdating }: MemberCardProps) {
  const [name, setName] = useState(member.name)
  const [email, setEmail] = useState(member.email)
  const [position, setPosition] = useState(member.position)
  const [linkedinUrl, setLinkedinUrl] = useState(member.linkedinUrl ?? '')
  const [imageUrl, setImageUrl] = useState(member.imageUrl)
  const { upload, isUploading } = useImageUpload()

  // Sync local state when member prop changes (after server update)
  useEffect(() => {
    setName(member.name)
    setEmail(member.email)
    setPosition(member.position)
    setLinkedinUrl(member.linkedinUrl ?? '')
    setImageUrl(member.imageUrl)
  }, [member.name, member.email, member.position, member.linkedinUrl, member.imageUrl])

  const handleBlur = (field: string, value: string) => {
    const currentValue = member[field as keyof typeof member]
    if (value !== currentValue) {
      onUpdate({ [field]: value || null })
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const url = await upload(file)
    setImageUrl(url)
    onUpdate({ imageUrl: url })
  }

  const handleClearImage = () => {
    setImageUrl(null)
    onUpdate({ imageUrl: null })
  }

  return (
    <div className="p-4 rounded-xl border border-border bg-input/50 space-y-4">
      <div className="flex items-start gap-4">
        <div className="relative">
          <label
            className={member.isLinked ? 'relative block' : 'cursor-pointer group relative block'}
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              disabled={isUploading || member.isLinked}
            />
            <Avatar className="size-16 ring-2 ring-border shrink-0 group-hover:ring-primary/50 transition-all">
              {imageUrl && <AvatarImage src={imageUrl} alt={name} />}
              <AvatarFallback className="bg-input text-lg font-medium text-white">
                {getInitials(name)}
              </AvatarFallback>
            </Avatar>
            {!member.isLinked && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-xs text-white">Edit</span>
              </div>
            )}
          </label>
          {imageUrl && !member.isLinked && (
            <button
              type="button"
              onClick={handleClearImage}
              disabled={isUpdating}
              className="absolute -top-1 -right-1 p-1 rounded-full bg-input border border-border text-muted-foreground hover:text-red-400 hover:border-red-400/50 transition-colors"
              title="Remove photo"
            >
              <X className="size-3" />
            </button>
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          <FormField label="Name">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => handleBlur('name', name)}
              placeholder="Full name"
              disabled={isUpdating || member.isLinked}
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Email">
              <TextInput
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur('email', email)}
                placeholder="email@example.com"
                type="email"
                disabled={isUpdating || member.isLinked}
              />
            </FormField>

            <FormField label="Role / Title">
              <TextInput
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                onBlur={() => handleBlur('position', position)}
                placeholder="e.g., CEO, CTO"
                disabled={isUpdating}
              />
            </FormField>
          </div>

          <FormField label="LinkedIn URL">
            <TextInput
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              onBlur={() => handleBlur('linkedinUrl', linkedinUrl)}
              placeholder="https://linkedin.com/in/username"
              disabled={isUpdating || member.isLinked}
            />
          </FormField>
        </div>

        <button
          type="button"
          onClick={onDelete}
          disabled={isUpdating}
          className="p-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-colors shrink-0"
          title="Remove member"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </div>
  )
}
